import { Box } from "@mui/material";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { DisplayQuotaUsage } from "../../App/Components/QuotaMessaging/QuotaMessaging";
import { useCandidateStopCompliance, useGetMessageCalendarConfigs } from "../../App/Queries/MessageQueries";
import { useMessageInputState } from "../../App/State/Hooks/MessageDialog/useMessageInput";
import { useMessageScheduleState } from "../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useUserProfileStore } from "../../App/State/Stores/UserProfileStore";
import { ICandidate } from "../../App/Typings/Candidate";
import { ICandidateProfile } from "../../App/Typings/CandidateProfile";
import { ICandidateWithMessageInfo, IMessageSendPost } from "../../App/Typings/MessageText";
import {
  NoPhoneNumbersAlert,
  NoValidPhoneNumbersAlert,
} from "../../MessageComponents/MessageDialog/Components/Alerts";
import { useValidateTagsAndRecipients } from "../../MessageComponents/MessageDialog/Components/Hooks/useValidateTagsAndRecipients";
import MessageHistory from "../../MessageComponents/MessageDialog/Components/MessageHistory";
import SendNowFooter from "../../MessageComponents/MessageDialog/Components/SendNowFooter";
import useMessageMutationState from "../../MessageComponents/MessageDialog/State/useMessageMutationState";
import { RegulationText } from "../../MessageComponents/SendGroupMessage/Components/SharedComponents/RegulationText";

export function MessageCenterCandidateText({
  candidate,
}: {
  candidate?: ICandidate | ICandidateProfile | ICandidateWithMessageInfo;
}) {
  const { profile } = useUserProfileStore();
  const [replyMessageId, setReplyMessageId] = useState<string>("");
  const [disableNotificationToggle, setDisableNotificationToggle] = useState<boolean>(false);
  const [notifyMe, setNotifyMe] = useState<boolean>(profile.notifyOnTextReply ?? false);

  const { body, bodyErrorMessage } = useMessageInputState();
  const { messageType, msgListQueue } = useMessageScheduleState();

  const { data: scheduleConfig, isLoading: scheduleConfigLoading } = useGetMessageCalendarConfigs(
    candidate?.clientId ?? "",
    candidate?.id ?? "",
    messageType,
  );
  const now = dayjs().utc();
  const isValidTime =
    now.isAfter(dayjs(scheduleConfig?.startDate)) && now.isBefore(dayjs(scheduleConfig?.endDate));

  const hasPhoneNumbers = Boolean(candidate?.phoneNumbers?.find((phoneNumber) => phoneNumber.isValid));

  const candidateId = candidate?.id || "";

  const { data: isStop } = useCandidateStopCompliance(candidateId);

  const sendMessageBody: IMessageSendPost = useMemo(
    () => ({
      candidates: [candidateId],
      isEmail: false,
      isText: true,
      isGroup: false,
      messages: msgListQueue,
      userId: "overrides in messagequeries",
      uiComponent: "overrides in messagequeries",
      userName: "",
      replyMessageId,
      notify: notifyMe,
      notifyEmail: "overrides in messagequeries",
    }),
    [candidateId, msgListQueue, replyMessageId, notifyMe],
  );

  const messageMutationState = useMessageMutationState(sendMessageBody);
  const {
    handleSendMessages,
    postSendMessageIsLoading,
    upsertMessageTemplateLoading,
    tempMessages,
    setTempMessages,
  } = messageMutationState;

  const canReceiveText = hasPhoneNumbers && !isStop;
  const { hasTitleError, recruiterTitleError, hasAreaOfInterestError, areaOfInterestError } =
    useValidateTagsAndRecipients("", "");
  const isValidMessage = !hasTitleError && !hasAreaOfInterestError && !!body.trim() && !bodyErrorMessage;

  const mutationLoading = postSendMessageIsLoading || upsertMessageTemplateLoading || scheduleConfigLoading;
  const disableMessageInput = !canReceiveText || mutationLoading || !scheduleConfig?.isValidNumber;
  const disableSendMenuButton = disableMessageInput || !isValidMessage;

  const dialogContentStyle = {
    display: "flex",
    height: "100%",
    flexDirection: "column",
  };

  let DialogContentDisplay = (
    <MessageHistory
      temporaryMessages={tempMessages}
      isStop={isStop || false}
      candidateId={candidateId}
      candidate={candidate}
      onTempMessageReloadLoad={() => setTempMessages([])}
      setReplyMessageId={setReplyMessageId}
      setDisableNotification={setDisableNotificationToggle}
    />
  );

  if (!hasPhoneNumbers) {
    DialogContentDisplay = (
      <>
        {candidate?.phoneNumbers && candidate?.phoneNumbers.length > 0 ? (
          <NoValidPhoneNumbersAlert />
        ) : (
          <NoPhoneNumbersAlert />
        )}
        {DialogContentDisplay}
      </>
    );
  } else if (!scheduleConfig?.isValidNumber) {
    DialogContentDisplay = <NoValidPhoneNumbersAlert message={scheduleConfig?.errorMessage} />;
  } else {
    dialogContentStyle["flexDirection"] = "column-reverse";
  }

  const customErrorMessage = hasTitleError
    ? recruiterTitleError
    : hasAreaOfInterestError
      ? areaOfInterestError
      : undefined;

  return (
    <>
      <Box
        data-test="message-text-not-dialog-window"
        sx={{
          width: "100%",
          height: "500px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        {DialogContentDisplay}
      </Box>
      <Box>
        <SendNowFooter
          disableInput={disableMessageInput}
          disableSendButton={disableSendMenuButton}
          disableNotificationToggle={disableNotificationToggle}
          handleSendMessages={handleSendMessages}
          isValidTime={isValidTime}
          notifyMe={notifyMe}
          setNotifyMe={setNotifyMe}
          RegulationTextComponent={<RegulationText sx={{ ml: "auto" }} />}
          customErrorMessage={customErrorMessage}
          QuotaUsageComponent={
            <DisplayQuotaUsage
              channel="text"
              recipientCount={1}
              isScheduled={false}
              sx={{ display: "block", maxWidth: "100%", textAlign: "right", ml: "auto" }}
            />
          }
        />
      </Box>
    </>
  );
}
